<template>
	<modal-dialog
		:modal-visible="visible"
		title="Import Certificates"
		:submitVisible="false"
		modalSize="modal-lg"
		v-on:closeModal="closeModal">

		<template v-if="!fileConfirmed">

			This allows you to upload a list of certifications.

			<h4 class="mt-4">Fields</h4>

			<div class="d-flex flex-row flex-wrap">
				<div class="col-md-6">
					<ul>
						<template v-for="(field, index) in requiredFields">
							<li v-if="index < (requiredFields.length / 2)">
								<span v-html="stripInlineEventHandlers(field.name)" ></span>
								<span v-if="'description' in field && field.description">
								 - 
								<span v-html="stripInlineEventHandlers(field.description)"></span>
							</span>
							</li>
						</template>
					</ul>
				</div>

				<div class="col-md-6">
					<ul>
						<template v-for="(field, index) in requiredFields">
							<li v-if="index >= (requiredFields.length / 2)">
								<span v-html="stripInlineEventHandlers(field.name)"></span>
								<span v-if="'description' in field && field.description">
								 - 
								<span v-html="stripInlineEventHandlers(field.description)"></span>
							</span>
							</li>
						</template>
					</ul>
				</div>
			</div>


			<div class="d-flex flex-row justify-content-between align-items-end mt-3">

				<h4>Example (.csv)</h4>

				<button class="btn btn-light btn-sm mb-3" v-on:click="downloadTemplate">
					<i class="fa fa-download"></i>
					Download Template
				</button>

			</div>

			<div style="overflow-x: auto;">
				<table class="spreadsheet-table w-100 mb-3">

					<tr>
						<th></th>
						<th v-for="(field, index) in requiredFields">
							{{numAsChar(index + 1)}}
						</th>
					</tr>

					<tr>
						<th>1</th>
						<td v-for="field in requiredFields">{{field.name}}</td>
					</tr>

					<tr v-for="(row, index) in exampleCSV">
						<th>{{index + 2}}</th>
						<td v-for="field in row">{{field}}</td>
					</tr>

				</table>
			</div>

			<div class="d-flex no-gutters justify-content-between align-items-baseline">
				<div class="col-10">
					<input type="file" class="form-control" v-on:change="uploadFile">
				</div>
				<div class="col-2 text-right">
					<button class="btn btn-primary" :disabled="!fileRows.length" v-on:click="setCertificates">
						Next
						<i class="fa fa-arrow-right"></i>
					</button>
				</div>
			</div>
		</template>

		<template v-else>

			<div v-for="(certificate, index) in certificates" class="card mb-3">
				<div class="card-body">

					<h3>Certificate #{{index + 1}}</h3>

					<div class="d-flex justify-content-between no-gutters flex-wrap mb-3">

						<div class="col-md-6 pr-3">
							<form-group label="Front of Certificate <span class='text-danger'>*</span>" col-class="stacked">
								<upload-field-with-panel
									api-model="CertificateUpload"
									upload-type="image/pdf"
									v-on:uploaded="fileUploaded($event,certificate, false)"
									v-on:uploadStarted="uploadStarted"
									v-on:deleted="fileDeleted(certificate, false)">
								</upload-field-with-panel>
							</form-group>
						</div>

						<div class="col-md-6 pl-3">
							<form-group label="Back of Certificate" col-class="stacked">
								<upload-field-with-panel
									api-model="CertificateUpload"
									upload-type="image/pdf"
									v-on:uploaded="fileUploaded($event, certificate, true)"
									v-on:uploadStarted="uploadStarted"
									v-on:deleted="fileDeleted(certificate, true)">
								</upload-field-with-panel>
							</form-group>
						</div>
					</div>

					<field label="Certificate Name"
							 col-class="col-md-5"
							 v-model="certificate.cert_name"
							 :readonly="true">
					</field>

					<field label="Issue Date"
							 col-class="col-md-5"
							 v-model="certificate.issue_date"
							 :readonly="true">
					</field>

					<field label="Apply to Employer Certificate"
						 style="flex-grow: 1"
						 col-class="col-md-5"
						 v-model="certificate.employerCertId"
						 :params="{type:'select',options:companyCertOptions}">
					</field>
				</div>
			</div>

			<div class="d-flex justify-content-end align-items-baseline flex-wrap">
				<span v-if="!allHaveFrontImage" class="text-danger mr-4">
					* One or more certificates are missing a front image
				</span>

				<button class="btn btn-primary" :disabled="!allHaveFrontImage" v-on:click="saveCertificates">
					Save
				</button>
			</div>




		</template>

	</modal-dialog>
</template>

<script type="text/javascript">

import ModalDialog from "../ModalDialog"
import Field from "@/components/Field"
import UploadFieldWithPanel from "@/components/super_components/UploadFieldWithPanel"
import FormGroup from "@/components/FormGroup"
	
export default {
	name: "EmployeeCertificateImportModal",
	components: {
		ModalDialog,
		Field,
		UploadFieldWithPanel,
		FormGroup
	},
	props: [
		"visible",
		"companyCertificates"
	],
	data() {
		return {

			requiredFields: [
				{ name: 'Certificate Name',          key: 'cert_name',          description: false },
				{ name: 'Certificate Number',        key: 'cert_number',        description: false },
				{ name: 'Issue Date',                key: 'issue_date',         description: 'in the format "YYYY-MM-DD"'},
				{ name: 'Never Expires?',            key: 'never_expires',      description: 'TRUE or FALSE'},
				{ name: 'Expiry Date',               key: 'expiry_date',        description: 'in the format "YYYY-MM-DD"' },
				{ name: 'Issuing Organization',      key: 'issuing_org',        description: false },
				{ name: 'Instructor/Issuer Name',    key: 'instructor_name',    description: false },
				{ name: 'Training/Issue Location',   key: 'training_location',  description: false }
			],

			exampleCSV: [
				['Worker Health & Safety Awareness',  '',  '2021-07-12', 'TRUE',  '',           'Ministry of Labour',         'Ministry of Labour', 'Online'],
				['Drivers License',  'T0293-29452-29385',  '2014-11-12', 'FALSE', '2019-11-12', 'Ministry of Transportation', '',                   'London, ON'],
			],

			file: null,					// The file the user uploaded
			fileHeaders: [],        // First row of CSV
			fileRows: [],           // Next rows of CSV
			fileConfirmed: false,

			certificates: [],           // set based on the uploaded file
			imageUploadsInProgress: 0,  // how many front/back images are we currently uploading?
			imageIds: []                // uploaded image ids (used to batch delete if modal is cancelled)
		}
	},
	watch: {
		visible() {
			this.resetImport()
		}
	},
	computed: {
		allHaveFrontImage() {
			this.imageIds.length  // unofficial key
			return this.certificates.every(cert => ('front_image_id' in cert && cert.front_image_id > 0))
		},
		companyCertOptions() {
			return this.optionify(this.companyCertificates,"cert_name")
		}
	},
	methods: {
		saveCertificates() {
			let vm = this

			const payloadCerts = this.cloneObj(this.certificates).map(cert => {
				delete cert.employerCertId
				return cert
			})

			const payload = {
				records: JSON.stringify(payloadCerts)
			}

			this.postData("UserCertificate/save-batch", payload, true).then(response => {
				if (response.success) {

					// Now package and save company certificates

					const payloadLinks = vm.certificates.map((cert, index) => {
						return {
							tenant_cert_id: cert.employerCertId,
							user_cert_id: response.data.records_saved_list[index]
						}
					})

					const companyPayload = {
						records: JSON.stringify(payloadLinks)
					}

					this.postData("UserCompanyCertificate/save-batch", companyPayload).then(() => {
						vm.alertSaved("Certificates saved!")
						vm.resetImport()
						vm.$emit('saved')
					})

				}
			})
		},

		fileUploaded(response, certificate, is_back) {
			if (is_back)
				certificate.back_image_id = response.data.id
			else
				certificate.front_image_id = response.data.id

			this.imageUploadsInProgress--
			this.imageIds.push(response.data.id)
		},

		fileDeleted(certificate, is_back) {
			const key = (is_back) ? 'back_image_id' : 'front_image_id'
			const index = this.imageIds.findIndex(id => id == certificate[key])
			this.imageIds.splice(index, 1)
			delete certificate[key]
		},

		uploadStarted() {
			this.imageUploadsInProgress++
		},

		setCertificates() {
			let vm = this

			const mappings = {}
			this.requiredFields.forEach(field => {
				const fileIndex = this.fileHeaders.findIndex(header => header == field.name)
				mappings[fileIndex] = field.key     // ex. 0 : 'issue_date'
			})

			this.certificates = this.fileRows.map(row => {
				let cert = { user_id: this.$store.userDetails.id }
				row.forEach((col, index) => {
					cert[mappings[index]] = col
				})
				return cert
			})

			this.fileConfirmed = true
		},
		uploadFile(event) {
			let vm = this

			if (event.target.files.length > 0)
				this.file = event.target.files[0]

			let f = this.file;

			let reader = new FileReader();

			let allowedMimes = [
				"text/plain",
				"text/csv",
				"application/vnd.ms-excel"
			]

			//check file extension type
			if (allowedMimes.indexOf(f.type) == -1)
				vm.alertFail("Invalid file format! File must be a CSV")

			else {

				// Closure to capture the file information.
				reader.onload = (theFile => {
					return e => {

						let csvObj = vm.CSVToArray(e.target.result)

						// Validate that we have at least one data row
						if (csvObj.length == 0) {
							vm.alertFail("File must have at least one data row!")
							return
						}

						// Remove header row from csvObj
						vm.fileHeaders = csvObj[0]
						csvObj.splice(0, 1)

						// Remove all completely empty rows
						let removeIndices = []
						csvObj.forEach((row, index) => {
							if (row.every(val => val === ''))
								removeIndices.push(index)
						})

						for (let i = removeIndices.length-1; i >= 0; i--)
							csvObj.splice(removeIndices[i], 1)

						// Check if last row is a blank, if so remove it
						if (csvObj[csvObj.length-1].length == 1)
							csvObj.splice(csvObj.length-1,1)

						// Validate that we have all of the required columns
						const requiredFieldNames = vm.requiredFields.map(field => field.name)
						const hasRequired = requiredFieldNames.every(header => vm.fileHeaders.includes(header))
						if (!hasRequired) {
							vm.alertFail("File is missing one or more columns!")
							return
						}

						vm.fileRows = csvObj
					};
				})(f);

				reader.readAsText(f);
			}
		},
		resetImport() {
			this.file = null
			this.fileHeaders = []
			this.fileRows = []
			this.fileConfirmed = false
			this.certificates = []
			this.imageUploadsInProgress = 0
			this.imageIds = []
		},
		downloadTemplate() {
			const titleRow = [this.requiredFields.map(field => field.name)]
			const template = titleRow.concat(this.exampleCSV)
			this.exportToCSV('template.csv', template)
		},
		closeModal() {
			// delete all uploaded files
			let promises = this.imageIds.map(id => this.postData("CertificateUpload/delete", {id}))
			Promise.all(promises).then(responses => {
				this.$emit("closeModal")
			})
		},
	},
	created() {

	}
}	
</script>